export interface MilitaryBase {
  slug: string;
  name: string;
  branch: string;
  city: string;
  state: string;
  stateSlug: string;
  region: string;
  /** Nearest large metro carriers actually route through. */
  metro: string;
  /** One or two sentences unique to this installation. */
  intro: string;
  /** Practical, base-specific logistics notes. */
  notes: string[];
  /** Common PCS lanes in and out of this installation. */
  lanes: string[];
}

export const MILITARY_BASES: MilitaryBase[] = [
  {
    slug: "fort-liberty",
    name: "Fort Liberty",
    branch: "U.S. Army",
    city: "Fayetteville",
    state: "North Carolina",
    stateSlug: "north-carolina",
    region: "Southeast",
    metro: "Fayetteville / Raleigh",
    intro:
      "Fort Liberty moves an enormous volume of PCS orders every summer, and the Fayetteville area sees carrier capacity tighten from late May through August. Booking three to six weeks out is the difference between a normal rate and a surge rate.",
    notes: [
      "Door-to-door pickup at on-post housing usually happens at a nearby commercial lot — 53-foot carriers cannot maneuver most housing loops",
      "All Americans Freeway and Bragg Boulevard give carriers straightforward interstate access to I-95",
      "Summer PCS season here is the busiest in the Army; hard delivery dates should be padded by two to three days",
      "Bring your orders and military ID to pickup if a spouse or family member is releasing the vehicle",
    ],
    lanes: [
      "Fort Liberty to Fort Cavazos, Texas",
      "Fort Liberty to Joint Base Lewis-McChord, Washington",
      "Fort Liberty to Fort Carson, Colorado",
      "Fort Liberty to Cape Coral and Fort Myers, Florida",
    ],
  },
  {
    slug: "fort-cavazos",
    name: "Fort Cavazos",
    branch: "U.S. Army",
    city: "Killeen",
    state: "Texas",
    stateSlug: "texas",
    region: "South Central",
    metro: "Killeen / Austin",
    intro:
      "Fort Cavazos is one of the largest armored posts in the country and one of the easiest installations to serve — I-35 runs straight past Killeen, so carriers have constant coverage in both directions.",
    notes: [
      "Killeen and Copperas Cove both have open commercial lots carriers prefer over on-post pickup",
      "I-35 north-south capacity is strong year round, which keeps rates competitive even in peak season",
      "Austin-area traffic can push a scheduled afternoon pickup into the evening window",
      "Multi-vehicle household moves are common here and usually earn a per-unit discount",
    ],
    lanes: [
      "Fort Cavazos to Fort Bliss, Texas",
      "Fort Cavazos to Fort Campbell, Kentucky",
      "Fort Cavazos to Joint Base Lewis-McChord, Washington",
      "Fort Cavazos to Fort Liberty, North Carolina",
    ],
  },
  {
    slug: "joint-base-lewis-mcchord",
    name: "Joint Base Lewis-McChord",
    branch: "U.S. Army / U.S. Air Force",
    city: "Tacoma",
    state: "Washington",
    stateSlug: "washington",
    region: "Pacific Northwest",
    metro: "Tacoma / Seattle",
    intro:
      "JBLM sits directly on I-5 between Tacoma and Olympia, which makes it one of the more predictable West Coast installations for vehicle pickup — but it is also a frequent origin for overseas orders routed through the Port of Tacoma.",
    notes: [
      "I-5 corridor carriers run JBLM constantly; northbound and southbound coverage is reliable",
      "Winter passes over the Cascades and Rockies can add transit days on eastbound moves",
      "If your orders send you overseas, we can deliver to the port terminal rather than a residence",
      "Rain-season pickups still happen; enclosed transport is worth it for classics and exotics leaving the Northwest",
    ],
    lanes: [
      "JBLM to Fort Cavazos, Texas",
      "JBLM to Fort Liberty, North Carolina",
      "JBLM to Schofield Barracks, Hawaii",
      "JBLM to Naval Base San Diego, California",
    ],
  },
  {
    slug: "fort-carson",
    name: "Fort Carson",
    branch: "U.S. Army",
    city: "Colorado Springs",
    state: "Colorado",
    stateSlug: "colorado",
    region: "Mountain West",
    metro: "Colorado Springs / Denver",
    intro:
      "Fort Carson and the wider Colorado Springs military community — Peterson, Schriever, the Air Force Academy — create steady year-round PCS volume along the I-25 corridor.",
    notes: [
      "Winter storms on I-70 and Raton Pass are the main cause of transit delays out of Colorado",
      "Denver is the nearest major carrier hub; a 60-mile reposition can open earlier pickup dates",
      "Altitude and cold starts: make sure the battery is healthy before an inoperable classification",
      "Lifted trucks are common here — tell us the height, because it changes which trucks can load you",
    ],
    lanes: [
      "Fort Carson to Fort Cavazos, Texas",
      "Fort Carson to Fort Liberty, North Carolina",
      "Fort Carson to Joint Base Lewis-McChord, Washington",
      "Fort Carson to Fort Campbell, Kentucky",
    ],
  },
  {
    slug: "fort-campbell",
    name: "Fort Campbell",
    branch: "U.S. Army",
    city: "Clarksville",
    state: "Tennessee",
    stateSlug: "tennessee",
    region: "Southeast",
    metro: "Clarksville / Nashville",
    intro:
      "Fort Campbell straddles the Kentucky–Tennessee line and sits about 50 miles from Nashville, one of the strongest carrier hubs in the country. That proximity keeps rates in and out of Campbell reasonable.",
    notes: [
      "Nashville's freight density means short-notice pickups are more achievable here than at most posts",
      "I-24 gives carriers a clean run to Atlanta, St. Louis and Chicago",
      "Gate access rules change; we plan a nearby off-post meeting point by default",
      "Deployment-cycle surges create bursts of demand that are not tied to normal PCS season",
    ],
    lanes: [
      "Fort Campbell to Fort Cavazos, Texas",
      "Fort Campbell to Fort Carson, Colorado",
      "Fort Campbell to Fort Stewart, Georgia",
      "Fort Campbell to Joint Base Lewis-McChord, Washington",
    ],
  },
  {
    slug: "fort-stewart",
    name: "Fort Stewart",
    branch: "U.S. Army",
    city: "Hinesville",
    state: "Georgia",
    stateSlug: "georgia",
    region: "Southeast",
    metro: "Hinesville / Savannah",
    intro:
      "Fort Stewart and Hunter Army Airfield anchor the Savannah-area military community, with I-95 running just east of the installation — a heavily trafficked lane for both PCS moves and snowbird season.",
    notes: [
      "I-95 is the busiest north-south auto transport lane in the country; capacity is good, timing is competitive",
      "Savannah's port draws freight traffic that competes for the same trucks in fall",
      "Coastal humidity and salt air make enclosed transport worth considering for collector vehicles",
      "Hinesville pickups are usually easier from a shopping-center lot than from housing",
    ],
    lanes: [
      "Fort Stewart to Fort Liberty, North Carolina",
      "Fort Stewart to Cape Coral and Fort Myers, Florida",
      "Fort Stewart to Fort Cavazos, Texas",
      "Fort Stewart to Fort Drum, New York",
    ],
  },
  {
    slug: "fort-bliss",
    name: "Fort Bliss",
    branch: "U.S. Army",
    city: "El Paso",
    state: "Texas",
    stateSlug: "texas",
    region: "Southwest",
    metro: "El Paso",
    intro:
      "Fort Bliss covers an enormous footprint on the west Texas–New Mexico line. El Paso is a major I-10 freight city, so east-west coverage is strong even though the region is remote.",
    notes: [
      "I-10 gives reliable service toward Phoenix, Tucson, San Antonio and Houston",
      "Northbound lanes to Denver and Salt Lake are thinner and price higher per mile",
      "Desert heat: check tire pressure and coolant before a summer pickup",
      "Border-region checkpoints can add a few hours to a driver's day, not to your total transit",
    ],
    lanes: [
      "Fort Bliss to Fort Cavazos, Texas",
      "Fort Bliss to Fort Carson, Colorado",
      "Fort Bliss to Naval Base San Diego, California",
      "Fort Bliss to Fort Liberty, North Carolina",
    ],
  },
  {
    slug: "naval-base-san-diego",
    name: "Naval Base San Diego",
    branch: "U.S. Navy",
    city: "San Diego",
    state: "California",
    stateSlug: "california",
    region: "West Coast",
    metro: "San Diego",
    intro:
      "San Diego's Navy and Marine Corps community — Naval Base San Diego, North Island, Camp Pendleton up the coast — produces constant vehicle movement, including a steady flow of cars headed to and from Hawaii.",
    notes: [
      "Southern California is a high-volume origin, so eastbound rates are generally favorable",
      "Deployment timelines mean many customers need storage-adjacent scheduling; tell us early",
      "Vehicles bound for Hawaii can be delivered to the port terminal instead of a residence",
      "Narrow downtown and Coronado streets require a nearby meet point for full-size carriers",
    ],
    lanes: [
      "Naval Base San Diego to Norfolk, Virginia",
      "Naval Base San Diego to Joint Base Pearl Harbor-Hickam, Hawaii",
      "Naval Base San Diego to Jacksonville, Florida",
      "Naval Base San Diego to Fort Cavazos, Texas",
    ],
  },
  {
    slug: "camp-pendleton",
    name: "Marine Corps Base Camp Pendleton",
    branch: "U.S. Marine Corps",
    city: "Oceanside",
    state: "California",
    stateSlug: "california",
    region: "West Coast",
    metro: "Oceanside / San Diego",
    intro:
      "Camp Pendleton spans 17 miles of coastline along I-5 between San Diego and Orange County. Carrier access is excellent, but on-base pickup is rarely practical for a full-size truck.",
    notes: [
      "Oceanside and Vista commercial lots are the standard pickup points",
      "I-5 north and I-15 east both serve the base directly",
      "Summer PCS overlaps with California tourist traffic; build in schedule buffer",
      "Many Pendleton customers are shipping a second vehicle to Hawaii or Okinawa via port delivery",
    ],
    lanes: [
      "Camp Pendleton to Camp Lejeune, North Carolina",
      "Camp Pendleton to Joint Base Pearl Harbor-Hickam, Hawaii",
      "Camp Pendleton to MCAS Cherry Point, North Carolina",
      "Camp Pendleton to Fort Cavazos, Texas",
    ],
  },
  {
    slug: "camp-lejeune",
    name: "Marine Corps Base Camp Lejeune",
    branch: "U.S. Marine Corps",
    city: "Jacksonville",
    state: "North Carolina",
    stateSlug: "north-carolina",
    region: "Southeast",
    metro: "Jacksonville, NC / Wilmington",
    intro:
      "Camp Lejeune sits about 50 miles off I-95 on the North Carolina coast. That detour is the single biggest factor in pricing here — carriers price the deviation, not just the miles.",
    notes: [
      "Being off the I-95 spine means fewer trucks pass by; three to six weeks of lead time matters more here",
      "Pairing pickup with a Wilmington or Jacksonville commercial lot shortens the deviation and can lower the rate",
      "Hurricane season can suspend coastal pickups for a few days at a time",
      "Cross-country west coast lanes are common and generally price better westbound in late summer",
    ],
    lanes: [
      "Camp Lejeune to Camp Pendleton, California",
      "Camp Lejeune to Fort Cavazos, Texas",
      "Camp Lejeune to Cape Coral and Fort Myers, Florida",
      "Camp Lejeune to MCB Quantico, Virginia",
    ],
  },
  {
    slug: "naval-station-norfolk",
    name: "Naval Station Norfolk",
    branch: "U.S. Navy",
    city: "Norfolk",
    state: "Virginia",
    stateSlug: "virginia",
    region: "Mid-Atlantic",
    metro: "Norfolk / Virginia Beach",
    intro:
      "Norfolk is the largest naval installation in the world and the densest concentration of military vehicle moves on the East Coast. Carrier availability is excellent in every direction.",
    notes: [
      "Hampton Roads tunnels and bridges route large carriers onto specific corridors; expect a meet point",
      "Sea-duty rotations create demand spikes independent of summer PCS season",
      "Port delivery is available for overseas orders instead of residential delivery",
      "I-64 and I-95 give strong coverage to the Northeast, Southeast and Midwest",
    ],
    lanes: [
      "Norfolk to Naval Base San Diego, California",
      "Norfolk to Jacksonville, Florida",
      "Norfolk to Joint Base Pearl Harbor-Hickam, Hawaii",
      "Norfolk to Fort Cavazos, Texas",
    ],
  },
  {
    slug: "joint-base-san-antonio",
    name: "Joint Base San Antonio",
    branch: "U.S. Air Force / U.S. Army",
    city: "San Antonio",
    state: "Texas",
    stateSlug: "texas",
    region: "South Central",
    metro: "San Antonio",
    intro:
      "JBSA combines Lackland, Randolph and Fort Sam Houston into one of the busiest training and medical hubs in the military, which means constant short-notice vehicle movement in and out of San Antonio.",
    notes: [
      "San Antonio is a major freight crossroads at I-35 and I-10 — capacity is strong all year",
      "Basic training and tech school cycles create steady one-way vehicle shipments",
      "Medical PCS moves to and from Brooke Army Medical Center often need flexible delivery windows",
      "Summer heat: verify battery and tire condition before pickup",
    ],
    lanes: [
      "JBSA to Fort Cavazos, Texas",
      "JBSA to Nellis AFB, Nevada",
      "JBSA to Eglin AFB, Florida",
      "JBSA to Joint Base Lewis-McChord, Washington",
    ],
  },
  {
    slug: "eglin-air-force-base",
    name: "Eglin Air Force Base",
    branch: "U.S. Air Force",
    city: "Valparaiso",
    state: "Florida",
    stateSlug: "florida",
    region: "Southeast",
    metro: "Fort Walton Beach / Pensacola",
    intro:
      "Eglin anchors the Florida Panhandle's military corridor alongside Hurlburt Field and NAS Pensacola. As a Florida company, this is home-state territory for us.",
    notes: [
      "I-10 runs directly across the Panhandle; east-west coverage is excellent",
      "Beach-season traffic on Highway 98 slows local pickup windows in summer",
      "Panhandle-to-peninsula moves to Cape Coral, Fort Myers and Tampa are routine for us",
      "Hurricane season can pause coastal pickups; we reschedule rather than risk the vehicle",
    ],
    lanes: [
      "Eglin AFB to Cape Coral and Fort Myers, Florida",
      "Eglin AFB to Joint Base San Antonio, Texas",
      "Eglin AFB to Fort Liberty, North Carolina",
      "Eglin AFB to Nellis AFB, Nevada",
    ],
  },
  {
    slug: "macdill-air-force-base",
    name: "MacDill Air Force Base",
    branch: "U.S. Air Force",
    city: "Tampa",
    state: "Florida",
    stateSlug: "florida",
    region: "Southeast",
    metro: "Tampa",
    intro:
      "MacDill sits on a peninsula in south Tampa and hosts CENTCOM and SOCOM. It is one of our closest installations and a lane we run constantly from Southwest Florida.",
    notes: [
      "South Tampa streets are tight; we stage pickups at a nearby commercial lot",
      "I-75 and I-4 give strong coverage north and east",
      "Snowbird season overlaps with PCS demand in Florida — book earlier than you would elsewhere",
      "We are based near Cape Coral in Southwest Florida, roughly two hours south, so local coordination is direct",
    ],
    lanes: [
      "MacDill AFB to Cape Coral and Fort Myers, Florida",
      "MacDill AFB to Norfolk, Virginia",
      "MacDill AFB to Fort Cavazos, Texas",
      "MacDill AFB to Joint Base Lewis-McChord, Washington",
    ],
  },
  {
    slug: "jacksonville-naval-air-station",
    name: "NAS Jacksonville",
    branch: "U.S. Navy",
    city: "Jacksonville",
    state: "Florida",
    stateSlug: "florida",
    region: "Southeast",
    metro: "Jacksonville",
    intro:
      "NAS Jacksonville and nearby Naval Station Mayport make Jacksonville one of the East Coast's largest Navy communities, right on the I-95 corridor.",
    notes: [
      "I-95 and I-10 intersect here, giving both north-south and east-west coverage",
      "Mayport sea-duty rotations create bursts of vehicle shipments outside normal PCS season",
      "Vehicles headed overseas can be delivered directly to the port terminal",
      "Southbound snowbird traffic in October through December competes for the same trucks",
    ],
    lanes: [
      "NAS Jacksonville to Norfolk, Virginia",
      "NAS Jacksonville to Naval Base San Diego, California",
      "NAS Jacksonville to Cape Coral and Fort Myers, Florida",
      "NAS Jacksonville to Fort Cavazos, Texas",
    ],
  },
  {
    slug: "fort-drum",
    name: "Fort Drum",
    branch: "U.S. Army",
    city: "Watertown",
    state: "New York",
    stateSlug: "new-york",
    region: "Northeast",
    metro: "Watertown / Syracuse",
    intro:
      "Fort Drum is the northernmost major Army post in the lower 48, and winter is a genuine logistics factor. Snowbelt weather between November and March regularly affects pickup timing.",
    notes: [
      "Lake-effect snow can stop pickups for a day or two; we build that into the window",
      "Syracuse, about 70 miles south, is the nearest reliable carrier hub",
      "Winter road salt is hard on vehicles — enclosed transport is worth the premium for anything collectible",
      "Southbound lanes to Florida are heavily booked October through December",
    ],
    lanes: [
      "Fort Drum to Cape Coral and Fort Myers, Florida",
      "Fort Drum to Fort Liberty, North Carolina",
      "Fort Drum to Fort Cavazos, Texas",
      "Fort Drum to Joint Base Lewis-McChord, Washington",
    ],
  },
  {
    slug: "joint-base-elmendorf-richardson",
    name: "Joint Base Elmendorf-Richardson",
    branch: "U.S. Air Force / U.S. Army",
    city: "Anchorage",
    state: "Alaska",
    stateSlug: "alaska",
    region: "Alaska",
    metro: "Anchorage",
    intro:
      "JBER moves are a different kind of project. Vehicles travel by ocean carrier between Tacoma or Seattle and the Port of Alaska in Anchorage, with over-the-road transport on both ends.",
    notes: [
      "Standard routing is door-to-port in Tacoma, ocean sailing, then port-to-door in Anchorage",
      "Sailing schedules are weekly, not daily — a missed cutoff means a full week of delay",
      "Winter sailings and Alaska road conditions extend total door-to-door time considerably",
      "Vehicles must be clean, under a quarter tank and free of loose personal items for ocean transit",
    ],
    lanes: [
      "JBER to Joint Base Lewis-McChord, Washington",
      "JBER to Fort Carson, Colorado",
      "JBER to Fort Cavazos, Texas",
      "JBER to Cape Coral and Fort Myers, Florida",
    ],
  },
  {
    slug: "joint-base-pearl-harbor-hickam",
    name: "Joint Base Pearl Harbor-Hickam",
    branch: "U.S. Navy / U.S. Air Force",
    city: "Honolulu",
    state: "Hawaii",
    stateSlug: "hawaii",
    region: "Hawaii",
    metro: "Honolulu / Oahu",
    intro:
      "Pearl Harbor-Hickam is the largest joint installation in the Pacific, and vehicle moves here always involve an ocean leg through the Port of Honolulu.",
    notes: [
      "Mainland pickup, port drop in Long Beach, Oakland, Tacoma or the East Coast, then Honolulu pickup",
      "Ocean carriers require a clean vehicle, quarter tank of fuel and no personal belongings inside",
      "Neighbor-island delivery to Maui, Kauai or the Big Island adds an inter-island sailing",
      "Our Hawaii hub has the full port process, timelines and 2026 pricing",
    ],
    lanes: [
      "Pearl Harbor-Hickam to Naval Base San Diego, California",
      "Pearl Harbor-Hickam to Joint Base Lewis-McChord, Washington",
      "Pearl Harbor-Hickam to Norfolk, Virginia",
      "Pearl Harbor-Hickam to Camp Pendleton, California",
    ],
  },
  {
    slug: "schofield-barracks",
    name: "Schofield Barracks",
    branch: "U.S. Army",
    city: "Wahiawa",
    state: "Hawaii",
    stateSlug: "hawaii",
    region: "Hawaii",
    metro: "Central Oahu",
    intro:
      "Schofield Barracks in central Oahu is the Army's Hawaii hub. Every vehicle move here runs through the Port of Honolulu with a mainland over-the-road leg on the other end.",
    notes: [
      "Plan four to eight weeks total door-to-door depending on the mainland origin",
      "Central Oahu delivery from the Honolulu port is short but subject to island traffic windows",
      "Salt air and island humidity make a thorough pre-shipment photo record especially important",
      "Returning to the mainland: book the port cutoff before you book your flight",
    ],
    lanes: [
      "Schofield Barracks to Joint Base Lewis-McChord, Washington",
      "Schofield Barracks to Fort Carson, Colorado",
      "Schofield Barracks to Fort Cavazos, Texas",
      "Schofield Barracks to Fort Liberty, North Carolina",
    ],
  },
  {
    slug: "nellis-air-force-base",
    name: "Nellis Air Force Base",
    branch: "U.S. Air Force",
    city: "Las Vegas",
    state: "Nevada",
    stateSlug: "nevada",
    region: "Southwest",
    metro: "Las Vegas",
    intro:
      "Nellis sits on the northeast side of Las Vegas, one of the strongest carrier markets in the West. Trucks pass through constantly on the I-15 corridor.",
    notes: [
      "I-15 gives fast coverage to Southern California, Salt Lake and Denver",
      "Las Vegas is one of the easier cities in the country for short-notice pickup",
      "Desert summer heat: confirm tire condition and battery health before pickup",
      "Exotic and collector vehicles are common here; enclosed capacity is readily available",
    ],
    lanes: [
      "Nellis AFB to Naval Base San Diego, California",
      "Nellis AFB to Joint Base San Antonio, Texas",
      "Nellis AFB to Fort Carson, Colorado",
      "Nellis AFB to Cape Coral and Fort Myers, Florida",
    ],
  },
  {
    slug: "wright-patterson-air-force-base",
    name: "Wright-Patterson Air Force Base",
    branch: "U.S. Air Force",
    city: "Dayton",
    state: "Ohio",
    stateSlug: "ohio",
    region: "Midwest",
    metro: "Dayton / Cincinnati",
    intro:
      "Wright-Patterson is the Air Force's research and logistics center, in a part of Ohio with excellent interstate access at the crossroads of I-70 and I-75.",
    notes: [
      "I-70 and I-75 make Dayton a natural carrier waypoint in every direction",
      "Midwest winters bring salt and freeze-thaw; enclosed is worth it for anything you care about",
      "Southbound Florida lanes book heavily October through December",
      "Short-notice pickups are usually achievable given the freight density",
    ],
    lanes: [
      "Wright-Patterson AFB to Cape Coral and Fort Myers, Florida",
      "Wright-Patterson AFB to Fort Cavazos, Texas",
      "Wright-Patterson AFB to Joint Base Lewis-McChord, Washington",
      "Wright-Patterson AFB to Norfolk, Virginia",
    ],
  },
  {
    slug: "mcb-quantico",
    name: "Marine Corps Base Quantico",
    branch: "U.S. Marine Corps",
    city: "Quantico",
    state: "Virginia",
    stateSlug: "virginia",
    region: "Mid-Atlantic",
    metro: "Northern Virginia / Washington, D.C.",
    intro:
      "Quantico sits directly on I-95 between Fredericksburg and Washington, D.C. Access is easy; the challenge is Northern Virginia traffic, which shapes every pickup window.",
    notes: [
      "Morning and evening rush on I-95 pushes pickups toward midday windows",
      "Nearby Stafford and Fredericksburg lots are usually faster than a residential attempt",
      "Proximity to D.C. means excellent Northeast and Southeast coverage",
      "School-year timing drives family PCS scheduling more than orders dates here",
    ],
    lanes: [
      "Quantico to Camp Lejeune, North Carolina",
      "Quantico to Camp Pendleton, California",
      "Quantico to Cape Coral and Fort Myers, Florida",
      "Quantico to Fort Cavazos, Texas",
    ],
  },
  {
    slug: "fort-belvoir",
    name: "Fort Belvoir",
    branch: "U.S. Army",
    city: "Fort Belvoir",
    state: "Virginia",
    stateSlug: "virginia",
    region: "Mid-Atlantic",
    metro: "Northern Virginia / Washington, D.C.",
    intro:
      "Fort Belvoir serves the National Capital Region and generates constant PCS movement to and from every corner of the country, plus frequent overseas port routing.",
    notes: [
      "Route 1 and I-95 access is good but congested; expect a scheduled meet point",
      "Overseas orders can be handled as door-to-port to Baltimore or Norfolk",
      "Government and contractor moves here often need documented delivery confirmation",
      "Peak summer PCS overlaps with regional tourism; book three to six weeks out",
    ],
    lanes: [
      "Fort Belvoir to Fort Cavazos, Texas",
      "Fort Belvoir to Joint Base Lewis-McChord, Washington",
      "Fort Belvoir to Cape Coral and Fort Myers, Florida",
      "Fort Belvoir to Fort Carson, Colorado",
    ],
  },
  {
    slug: "fort-riley",
    name: "Fort Riley",
    branch: "U.S. Army",
    city: "Junction City",
    state: "Kansas",
    stateSlug: "kansas",
    region: "Midwest",
    metro: "Manhattan / Topeka",
    intro:
      "Fort Riley sits on I-70 in northeast Kansas. It is rural, but the interstate keeps east-west carrier coverage workable in both directions year round.",
    notes: [
      "I-70 is the main artery; north-south lanes out of Kansas are thinner and price higher",
      "Kansas City, about two hours east, is the nearest major carrier hub",
      "Winter ice storms are the primary delay risk from December through February",
      "Rural pickup addresses are usually fine here — space is not the constraint it is on the coasts",
    ],
    lanes: [
      "Fort Riley to Fort Carson, Colorado",
      "Fort Riley to Fort Campbell, Kentucky",
      "Fort Riley to Fort Cavazos, Texas",
      "Fort Riley to Joint Base Lewis-McChord, Washington",
    ],
  },
];

export function getMilitaryBase(slug: string): MilitaryBase | undefined {
  return MILITARY_BASES.find((b) => b.slug === slug);
}

export const MILITARY_BASE_SLUGS = MILITARY_BASES.map((b) => b.slug);

/** Bases grouped by region for the hub index. */
export function basesByRegion(): { region: string; bases: MilitaryBase[] }[] {
  const order = [
    "Southeast",
    "Mid-Atlantic",
    "Northeast",
    "Midwest",
    "South Central",
    "Southwest",
    "Mountain West",
    "West Coast",
    "Pacific Northwest",
    "Alaska",
    "Hawaii",
  ];
  return order
    .map((region) => ({
      region,
      bases: MILITARY_BASES.filter((b) => b.region === region),
    }))
    .filter((g) => g.bases.length > 0);
}
